import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const MAX_POLLS = 12;       // 12 × 5s = 60s max wait
const POLL_INTERVAL = 5000; // 5 seconds

export default function PaymentCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const intentId = searchParams.get("intent");
    const paymentParam = searchParams.get("payment"); // 'error' or 'cancelled' from relay

    const [status, setStatus] = useState("polling"); // polling | completed | error | cancelled
    const [message, setMessage] = useState("Verifying your payment…");
    const pollCount = useRef(0);

    useEffect(() => {
        // If EPS already told us it failed/cancelled via relay param
        if (paymentParam === "error") {
            setStatus("error");
            setMessage("Payment failed. Please try again.");
            return;
        }
        if (paymentParam === "cancelled") {
            setStatus("cancelled");
            setMessage("Payment was cancelled.");
            return;
        }

        if (!intentId) {
            setStatus("error");
            setMessage("No payment reference found.");
            return;
        }

        const poll = async () => {
            pollCount.current += 1;
            try {
                const { data } = await api.get(`/billing/verify/${intentId}`);
                if (data.status === "completed") {
                    setStatus("completed");
                    setMessage("Payment confirmed! Redirecting…");
                    setTimeout(() => {
                        navigate("/dashboard/billing?payment=success", { replace: true });
                    }, 1800);
                    return;
                }
                // Keep polling if pending
                if (pollCount.current < MAX_POLLS) {
                    setTimeout(poll, POLL_INTERVAL);
                } else {
                    // Timed out — redirect to billing, user can check manually
                    setStatus("error");
                    setMessage("Payment verification timed out. Please check your billing page.");
                    setTimeout(() => {
                        navigate("/dashboard/billing?payment=hold", { replace: true });
                    }, 3000);
                }
            } catch {
                if (pollCount.current < MAX_POLLS) {
                    setTimeout(poll, POLL_INTERVAL);
                } else {
                    setStatus("error");
                    setMessage("Could not verify payment. Please check your billing page.");
                    setTimeout(() => {
                        navigate("/dashboard/billing", { replace: true });
                    }, 3000);
                }
            }
        };

        // Start polling after a brief delay to let EPS process on their end
        const timer = setTimeout(poll, 2000);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const icon = {
        polling:   <Loader2 className="w-14 h-14 text-coral animate-spin" />,
        completed: <CheckCircle2 className="w-14 h-14 text-mint" />,
        error:     <XCircle className="w-14 h-14 text-red-400" />,
        cancelled: <AlertTriangle className="w-14 h-14 text-amber-400" />,
    }[status];

    const subtitle = {
        polling:   "Please wait — this may take a few seconds.",
        completed: "Your credits have been applied to your account.",
        error:     "Something went wrong. You will be redirected shortly.",
        cancelled: "No charges were made.",
    }[status];

    return (
        <div className="min-h-screen bg-cream-100 flex flex-col items-center justify-center px-4">
            <div className="bg-white rounded-3xl shadow-soft border border-cream-300 p-12 flex flex-col items-center gap-6 max-w-md w-full text-center">

                {/* Logo */}
                <div className="w-10 h-10 rounded-xl bg-coral flex items-center justify-center mb-2">
                    <span className="font-display text-white text-[18px] font-bold">A</span>
                </div>

                {icon}

                <div>
                    <h1 className="font-display text-[22px] font-semibold text-ink mb-2">
                        {message}
                    </h1>
                    <p className="text-[14px] text-ink-muted">{subtitle}</p>
                </div>

                {status === "polling" && (
                    <p className="text-[12px] text-ink-muted">
                        Do not close or refresh this page.
                    </p>
                )}

                {(status === "error" || status === "cancelled") && (
                    <button
                        onClick={() => navigate("/dashboard/billing")}
                        className="mt-2 px-6 py-2.5 rounded-xl bg-coral text-white text-[14px] font-semibold hover:bg-coral/90 transition"
                    >
                        Go to Billing
                    </button>
                )}
            </div>

            <p className="mt-8 text-[12px] text-ink-muted">
                Secure payment via EPS · <span className="text-coral">audo.dev</span>
            </p>
        </div>
    );
}
